import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { api, imgUrl } from "@/lib/api";
import { trackVisit } from "@/lib/track";
import { LayoutDashboard, ArrowUpRight, Sparkles, Loader2, Star, ChevronLeft, ChevronRight, Check, Store, Zap, Info } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import DirectoryV2 from "@/pages/DirectoryV2";
import DirectoryV3 from "@/pages/DirectoryV3";
import DirectoryV4 from "@/pages/DirectoryV4";
import SignupForm from "@/components/SignupForm";
import CookieConsent from "@/components/CookieConsent";
import { openCookieSettings } from "@/lib/consent";

const HERO_IMG = "https://images.pexels.com/photos/8387837/pexels-photo-8387837.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const GLOW_IMG = "https://images.unsplash.com/photo-1645811791231-279658fb46dc?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const LEGAL_LINKS = [
  { slug: "terms", label: "Terms of Service" },
  { slug: "privacy", label: "Privacy Policy" },
  { slug: "cookies", label: "Cookie Policy" },
  { slug: "refunds", label: "Returns & Refunds" },
  { slug: "disclaimer", label: "Disclaimer" },
  { slug: "imprint", label: "Contact & Imprint" },
];
const DEFAULT_CONTENT = {
  hero_eyebrow: "The Instagram concept store",
  hero_title: "Shop the feed. Every store, one experience.",
  hero_subtitle: "Browse independent storefronts built like your favourite Instagram profiles — stories, grids, and instant checkout.",
  cta_primary_label: "Explore stores",
  cta_secondary_label: "Become a merchant",
  stores_heading: "Featured stores",
  footer_tagline: "The Instagram concept store platform.",
  hero_image: "",
  hero_video: "",
  reviews_heading: "Loved by merchants",
  reviews: [
    { name: "Maya Chen", handle: "@mayaruns", text: "Set up my whole shop in an afternoon. My sales tripled in the first month.", rating: 5 },
    { name: "Liam Porter", handle: "@solestories", text: "It genuinely feels like Instagram — my customers just get it. Checkout is instant.", rating: 5 },
    { name: "Noor Haddad", handle: "@glowbynoor", text: "Managing three storefronts from one dashboard is a dream. Analytics are gorgeous.", rating: 5 },
  ],
  pricing_heading: "Simple pricing",
  pricing: [
    { name: "Starter", price: "€0", period: "/mo", features: ["1 store", "Up to 20 products", "Real Stripe checkout"], cta: "Get started", highlight: false },
    { name: "Growth", price: "€29", period: "/mo", features: ["Unlimited stores", "Unlimited products", "Live analytics", "Priority support"], cta: "Start free trial", highlight: true },
    { name: "Scale", price: "€99", period: "/mo", features: ["Everything in Growth", "Custom domain", "Team accounts", "API access"], cta: "Contact sales", highlight: false },
  ],
  disclaimer: "StoreIG is an independent platform and is not affiliated with, endorsed by, sponsored by, or in any way officially connected to Instagram or Meta Platforms, Inc. \"Instagram\" is a trademark of Meta Platforms, Inc. All product images, store names and reviews shown are for demonstration purposes only.",
  brand_name: "CasperBrands",
  brand_url: "https://casperbrands.com",
  brand_logo_url: "https://customer-assets-agu9un31.emergentagent.net/job_store-dashboard-69/artifacts/z5d0msg2_LOGO-CASPERBRANDs.png",
};

const GRAIN = "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='140' height='140'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")";

const reveal = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 100, damping: 20 } },
};

export default function Directory() {
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [signupOpen, setSignupOpen] = useState(false);
  const [content, setContent] = useState(DEFAULT_CONTENT);
  const [contentLoaded, setContentLoaded] = useState(false);
  const [subscribing, setSubscribing] = useState(null);

  useEffect(() => {
    trackVisit("/");
    api.get("/public/stores").then((r) => setStores(r.data)).finally(() => setLoading(false));
    api.get("/public/site").then((r) => setContent({ ...DEFAULT_CONTENT, ...r.data })).catch(() => {}).finally(() => setContentLoaded(true));
  }, []);

  // Apply landing-page SEO/meta (lander only — shops manage their own SEO).
  useEffect(() => {
    if (!contentLoaded) return;
    const title = content.meta_title || "StoreIG — The Instagram Concept Store";
    const desc = content.meta_description || content.hero_subtitle || "";
    const ogImg = content.og_image
      ? (content.og_image.startsWith("http") ? content.og_image : `${window.location.origin}${imgUrl(content.og_image)}`)
      : "";
    document.title = title;
    const up = (sel, attr, key, val) => {
      if (!val) return;
      let el = document.head.querySelector(sel);
      if (!el) { el = document.createElement("meta"); el.setAttribute(attr, key); document.head.appendChild(el); }
      el.setAttribute("content", val);
    };
    up('meta[name="description"]', "name", "description", desc);
    up('meta[property="og:title"]', "property", "og:title", title);
    up('meta[property="og:description"]', "property", "og:description", desc);
    up('meta[property="og:type"]', "property", "og:type", "website");
    up('meta[property="og:image"]', "property", "og:image", ogImg);
    up('meta[name="twitter:card"]', "name", "twitter:card", ogImg ? "summary_large_image" : "summary");
    up('meta[name="twitter:title"]', "name", "twitter:title", title);
    up('meta[name="twitter:description"]', "name", "twitter:description", desc);
    up('meta[name="twitter:image"]', "name", "twitter:image", ogImg);
    if (content.favicon) {
      const href = content.favicon.startsWith("http") ? content.favicon : `${window.location.origin}${imgUrl(content.favicon)}`;
      let link = document.head.querySelector('link[rel="icon"]');
      if (!link) { link = document.createElement("link"); link.setAttribute("rel", "icon"); document.head.appendChild(link); }
      link.setAttribute("href", href);
    }
  }, [content, contentLoaded]);

  const handlePlan = async (p, i) => {
    const amount = parseFloat(String(p.price).replace(/[^\d.,]/g, "").replace(",", "."));
    if (content.subscriptions_enabled && amount > 0) {
      setSubscribing(i);
      try {
        const res = await api.post("/payments/subscribe", { plan_index: i, plan_id: p.id || "", origin_url: window.location.origin });
        window.location.href = res.data.checkout_url;
      } catch (e) {
        toast.error(e.response?.data?.detail || "Could not start checkout");
        setSubscribing(null);
      }
    } else {
      setSignupOpen(true);
    }
  };

  const [searchParams, setSearchParams] = useSearchParams();
  const rawPreview = searchParams.get("preview");
  const preview = ["v1", "v2", "v3", "v4"].includes(rawPreview) ? rawPreview : null;
  const effective = preview || content.frontpage_template || "v1";
  const pickPreview = (t) => { const sp = new URLSearchParams(searchParams); sp.set("preview", t); setSearchParams(sp, { replace: true }); };
  const PREVIEW_OPTS = [{ k: "v1", label: "Classic" }, { k: "v2", label: "Bold" }, { k: "v3", label: "Pop" }, { k: "v4", label: "Editorial" }];
  const previewBar = preview ? (
    <div data-testid="preview-bar" className="fixed bottom-5 left-1/2 -translate-x-1/2 z-[100] flex items-center gap-1.5 rounded-full bg-black/90 backdrop-blur-xl text-white pl-4 pr-1.5 py-1.5 shadow-2xl border border-white/10">
      <span className="text-[10px] uppercase tracking-[0.2em] text-white/50 mr-1">Preview</span>
      {PREVIEW_OPTS.map((t) => (
        <button key={t.k} onClick={() => pickPreview(t.k)} data-testid={`preview-pick-${t.k}`}
          className={`text-xs font-semibold rounded-full px-3 py-1.5 transition-colors ${effective === t.k ? "bg-white text-black" : "text-white/70 hover:text-white"}`}>
          {t.label}
        </button>
      ))}
      <span className="text-[10px] text-white/40 mx-1 hidden sm:inline">live: {(content.frontpage_template || "v1").toUpperCase()}</span>
      <a href="/" data-testid="preview-exit" className="text-xs font-semibold rounded-full px-3 py-1.5 bg-white/10 hover:bg-white/20 transition-colors">Exit</a>
    </div>
  ) : null;

  // Wait for the real site content before choosing a template — prevents a flash
  // where a default template paints first and then swaps to the configured one.
  if (!contentLoaded) {
    return (
      <div data-testid="directory-loading" className="min-h-screen bg-white flex items-center justify-center">
        <div className="h-7 w-7 rounded-full border-2 border-zinc-200 border-t-zinc-500 animate-spin" />
      </div>
    );
  }

  if (effective === "v4") {
    return <>{previewBar}<DirectoryV4 content={content} stores={stores} loading={loading} /><CookieConsent /></>;
  }
  if (effective === "v3") {
    return <>{previewBar}<DirectoryV3 content={content} stores={stores} loading={loading} /><CookieConsent /></>;
  }
  if (effective !== "v1") {
    return <>{previewBar}<DirectoryV2 content={content} stores={stores} loading={loading} /><CookieConsent /></>;
  }

  return (
    <div className="relative min-h-screen bg-[#FAFAFB] text-slate-900 flex flex-col overflow-x-clip">
      {/* grain texture */}
      <div className="pointer-events-none fixed inset-0 z-0 opacity-[0.05] mix-blend-multiply" style={{ backgroundImage: GRAIN }} aria-hidden />
      {previewBar}

      {/* Floating glass nav */}
      <div className="sticky top-0 z-50 px-4 md:px-8 pt-4">
        <header className="max-w-6xl mx-auto flex items-center justify-between rounded-full bg-white/70 backdrop-blur-2xl border border-white/60 shadow-[0_8px_32px_rgba(0,0,0,0.05)] pl-6 pr-2 h-16">
          <div className="font-heading font-bold text-2xl tracking-tight" data-testid="directory-brand">
            STORE<span className="casper-text">IG</span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setSignupOpen(true)} data-testid="signup-cta"
              className="hidden sm:flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-body font-semibold text-slate-600 hover:text-slate-900 transition-colors active:scale-95">
              <Sparkles size={16} /> Sign up
            </button>
            <Link to="/login" data-testid="admin-cta"
              className="flex items-center gap-2 casper-btn rounded-full px-5 py-2.5 text-sm font-body font-semibold active:scale-95">
              <LayoutDashboard size={16} /> Merchant Login
            </Link>
          </div>
        </header>
      </div>

      {/* Hero — asymmetric bento */}
      <section className="relative z-10 max-w-6xl mx-auto w-full px-6 pt-16 md:pt-24 pb-8">
        {/* ambient CasperBlue glow */}
        <div className="pointer-events-none absolute -top-10 right-0 w-[46rem] h-[46rem] -z-10 opacity-60"
          style={{ backgroundImage: `url(${GLOW_IMG})`, backgroundSize: "cover", filter: "blur(80px)" }} aria-hidden />

        <div className="grid lg:grid-cols-[1.05fr_0.95fr] gap-10 lg:gap-14 items-center">
          <motion.div initial="hidden" animate="show" variants={reveal}>
            <span className="inline-flex items-center gap-2 text-xs font-heading font-bold uppercase tracking-[0.22em]" data-testid="hero-eyebrow">
              <span className="h-1.5 w-1.5 rounded-full casper-gradient" />
              <span className="casper-text">{content.hero_eyebrow}</span>
            </span>
            <h1 className="font-heading font-semibold text-5xl md:text-6xl lg:text-7xl tracking-tighter leading-[1.03] mt-5 whitespace-pre-line" data-testid="hero-title">
              {content.hero_title}
            </h1>
            <p className="font-body text-slate-500 text-lg mt-6 max-w-md leading-relaxed" data-testid="hero-subtitle">
              {content.hero_subtitle}
            </p>
            <div className="mt-9 flex flex-wrap gap-3">
              <a href="#stores" className="casper-btn rounded-full px-7 py-3.5 text-sm font-body font-semibold active:scale-95">
                {content.cta_primary_label}
              </a>
              <button onClick={() => setSignupOpen(true)}
                className="bg-white border border-slate-200/80 rounded-full px-7 py-3.5 text-sm font-body font-semibold hover:border-slate-900 transition-colors active:scale-95 shadow-[0_8px_32px_rgba(0,0,0,0.04)]">
                {content.cta_secondary_label}
              </button>
            </div>
            {!loading && stores.length > 0 && (
              <div className="mt-9 flex items-center gap-3 text-sm text-slate-500 font-body">
                <div className="flex -space-x-3">
                  {stores.slice(0, 4).map((s) => (
                    <img key={s.id} src={imgUrl(s.logo_url)} alt="" className="h-8 w-8 rounded-full object-cover ring-2 ring-white bg-slate-100" />
                  ))}
                </div>
                <span><b className="text-slate-900">{stores.length}</b> live storefronts</span>
              </div>
            )}
          </motion.div>

          {/* Right — floating image bento */}
          <motion.div initial={{ opacity: 0, y: 30, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ type: "spring", stiffness: 90, damping: 20, delay: 0.1 }}
            className="relative">
            <div className="relative rounded-[2.5rem] overflow-hidden border border-white/60 shadow-[0_30px_80px_rgba(15,23,42,0.15)]">
              <img src={imgUrl(content.hero_image) || HERO_IMG} alt="Shop the feed" className="w-full h-[400px] md:h-[520px] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/25 via-transparent to-transparent" />
            </div>
            {/* floating glass card */}
            <div className="absolute -bottom-5 -left-5 bg-white/70 backdrop-blur-2xl border border-white/60 rounded-2xl px-5 py-4 shadow-[0_20px_48px_rgba(0,0,0,0.1)]">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl casper-gradient flex items-center justify-center text-white"><Zap size={18} /></div>
                <div className="font-body leading-tight">
                  <div className="text-sm font-semibold text-slate-900">Instant checkout</div>
                  <div className="text-xs text-slate-500">Real Stripe payments</div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Slim CasperBlue gradient stripe */}
      <div className="relative z-10 max-w-6xl mx-auto w-full px-6 my-16 md:my-24">
        <div className="h-1 w-full rounded-full casper-gradient" data-testid="gradient-stripe" />
      </div>

      {/* Stores — editorial grid */}
      <section id="stores" className="relative z-10 max-w-6xl mx-auto px-6 w-full flex-1">
        <div className="flex items-end justify-between mb-10">
          <div>
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">The directory</span>
            <h2 className="font-heading font-medium text-3xl md:text-4xl tracking-tight mt-2 flex items-center gap-3">
              <Store size={26} className="text-slate-300" /> {content.stores_heading}
            </h2>
          </div>
        </div>
        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (<div key={`sk-${i}`} className="h-64 rounded-3xl bg-slate-100 animate-pulse" />))}
          </div>
        ) : (
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }}
            variants={{ show: { transition: { staggerChildren: 0.06 } } }}
            className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="store-directory-grid">
            {stores.map((s) => (
              <motion.div key={s.id} variants={reveal}>
                <Link to={`/${s.handle}`} data-testid={`store-card-${s.handle}`}
                  className="group block bg-white rounded-3xl border border-slate-200/60 overflow-hidden hover:-translate-y-2 transition-transform duration-300 shadow-[0_8px_32px_rgba(0,0,0,0.04)] hover:shadow-[0_20px_48px_rgba(0,0,0,0.08)]">
                  <div className="h-24 relative" style={{ background: s.banner_url ? undefined : `linear-gradient(135deg, ${s.accent_color}22, ${s.accent_color}55)` }}>
                    {s.banner_url && <img src={imgUrl(s.banner_url)} alt="" className="w-full h-full object-cover" />}
                  </div>
                  <div className="px-6 pb-6 -mt-9">
                    <div className="flex items-end justify-between">
                      <div className="ig-ring p-[2.5px] rounded-full">
                        <div className="bg-white p-[2px] rounded-full">
                          <img src={imgUrl(s.logo_url)} alt={s.name} className="h-16 w-16 rounded-full object-cover bg-slate-100" />
                        </div>
                      </div>
                      <ArrowUpRight className="text-slate-300 group-hover:text-slate-900 transition-colors mb-2" />
                    </div>
                    <div className="font-heading font-semibold text-lg tracking-tight mt-3">{s.name}</div>
                    <div className="text-sm text-slate-400">@{s.handle}</div>
                    <p className="font-body text-slate-500 mt-3 text-sm leading-relaxed line-clamp-2">{s.tagline}</p>
                    <span className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-3 py-1"
                      style={{ backgroundColor: `${s.accent_color}14`, color: s.accent_color }}>
                      {s.product_count} products
                    </span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </motion.div>
        )}
      </section>

      {/* Pricing — high-contrast highlight */}
      {content.pricing?.length ? (
        <section className="relative z-10 max-w-6xl mx-auto px-6 pt-28 md:pt-32 w-full" data-testid="pricing-section">
          <div className="text-center mb-14">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">Pricing</span>
            <h2 className="font-heading font-medium text-3xl md:text-5xl tracking-tight mt-2">{content.pricing_heading}</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6 items-center">
            {content.pricing.map((p, i) => (
              <motion.div key={`price-${p.name}-${i}`} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ type: "spring", stiffness: 100, damping: 20, delay: i * 0.08 }}
                data-testid={`pricing-box-${i}`}
                className={p.highlight ? "relative md:-translate-y-4 md:scale-[1.04]" : ""}>
                {p.highlight && <div className="pointer-events-none absolute -inset-4 casper-gradient opacity-20 blur-2xl rounded-[2.5rem] -z-10" />}
                <div className={`rounded-3xl h-full flex flex-col ${p.highlight ? "p-[2px] casper-gradient shadow-[0_24px_60px_rgba(11,30,77,0.2)]" : ""}`}>
                  <div className={`rounded-3xl p-8 flex flex-col h-full ${p.highlight ? "bg-white" : "bg-slate-50/80 border border-slate-200/60"}`}>
                    <div className="flex items-center justify-between">
                      <span className="font-heading font-semibold text-lg">{p.name}</span>
                      {p.highlight && <span className="text-[10px] font-bold uppercase tracking-widest casper-text">Popular</span>}
                    </div>
                    <div className="mt-4 flex items-end gap-1">
                      <span className="font-heading font-semibold text-5xl tracking-tighter">{p.price}</span>
                      <span className="mb-1.5 text-sm text-slate-400">{p.period}</span>
                    </div>
                    <ul className="mt-6 space-y-3 flex-1">
                      {(p.features || []).map((f, fi) => (
                        <li key={`f-${fi}`} className="flex items-center gap-2.5 text-sm text-slate-600">
                          <span className="h-5 w-5 rounded-full flex items-center justify-center casper-gradient text-white shrink-0"><Check size={12} /></span> {f}
                        </li>
                      ))}
                    </ul>
                    <button onClick={() => handlePlan(p, i)} disabled={subscribing === i} data-testid={`pricing-cta-${i}`}
                      className={`mt-8 rounded-full py-3.5 text-sm font-body font-semibold transition-transform active:scale-95 flex items-center justify-center gap-2 disabled:opacity-60 ${p.highlight ? "casper-btn" : "bg-white border border-slate-200 text-slate-900 hover:border-slate-900"}`}>
                      {subscribing === i && <Loader2 size={15} className="animate-spin" />} {p.cta}
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </section>
      ) : null}

      {/* Reviews — glass social stream */}
      {content.reviews?.length ? (
        <ReviewsSlider heading={content.reviews_heading} reviews={content.reviews} />
      ) : null}

      {/* Footer — editorial anchor */}
      <footer className="relative z-10 mt-28 border-t border-slate-200/60 bg-white overflow-hidden">
        <div className="pointer-events-none absolute inset-x-0 bottom-0 flex justify-center leading-none select-none" aria-hidden>
          <span className="font-heading font-black text-[22vw] text-slate-900/[0.03] translate-y-[18%]">STOREIG</span>
        </div>
        <div className="relative max-w-6xl mx-auto px-6 py-16">
          <div className="grid gap-10 md:grid-cols-2 lg:grid-cols-4">
            <div>
              <div className="font-heading font-bold text-2xl tracking-tight">STORE<span className="casper-text">IG</span></div>
              <p className="text-sm text-slate-500 mt-3 max-w-xs font-body">{content.footer_tagline}</p>
            </div>
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400 mb-4">Explore</div>
              <ul className="space-y-2.5 text-sm font-body text-slate-500">
                <li><a href="#stores" className="hover:text-slate-900 transition-colors" data-testid="footer-stores">Stores</a></li>
                <li><button onClick={() => setSignupOpen(true)} className="hover:text-slate-900 transition-colors" data-testid="footer-signup">Become a merchant</button></li>
                <li><Link to="/login" className="hover:text-slate-900 transition-colors" data-testid="footer-login">Merchant login</Link></li>
              </ul>
            </div>
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400 mb-4">Legal</div>
              <ul className="space-y-2.5 text-sm font-body text-slate-500">
                {LEGAL_LINKS.map((l) => (
                  <li key={l.slug}><Link to={`/legal/${l.slug}`} data-testid={`footer-legal-${l.slug}`} className="hover:text-slate-900 transition-colors">{l.label}</Link></li>
                ))}
                <li><button onClick={openCookieSettings} data-testid="footer-cookie-settings" className="hover:text-slate-900 transition-colors">Cookie settings</button></li>
              </ul>
            </div>
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400 mb-4">More from us</div>
              <ul className="space-y-2.5 text-sm font-body text-slate-500">
                <li><a href="https://tiktokwebstore.com" target="_blank" rel="noreferrer" data-testid="footer-sister-tiktok" className="inline-flex items-center gap-1.5 hover:text-slate-900 transition-colors">TikTokWebstore.com <ArrowUpRight size={13} /></a></li>
                <li><a href="https://wawebstore.com" target="_blank" rel="noreferrer" data-testid="footer-sister-wa" className="inline-flex items-center gap-1.5 hover:text-slate-900 transition-colors">WAwebstore.com <ArrowUpRight size={13} /></a></li>
              </ul>
            </div>
          </div>
          <div className="mt-12 rounded-2xl border-2 border-slate-300 bg-white p-6 shadow-sm" data-testid="footer-affiliation-note">
            <div className="flex items-center gap-2 text-slate-800 mb-2">
              <Info size={17} strokeWidth={2} />
              <span className="text-xs font-bold uppercase tracking-[0.2em]">Independent platform</span>
            </div>
            <p className="text-sm text-slate-600 leading-relaxed max-w-3xl font-body font-medium" data-testid="footer-disclaimer">{content.disclaimer}</p>
          </div>
          <div className="mt-8 text-center text-xs text-slate-400">© {new Date().getFullYear()} StoreIG. All rights reserved. · A <a href={content.brand_url || "#"} target="_blank" rel="noreferrer" data-testid="footer-brand-name" className="hover:text-slate-900 transition-colors">{content.brand_name || "CasperBrands"}</a> company.</div>
        </div>
      </footer>

      {/* Sign up popup */}
      <Dialog open={signupOpen} onOpenChange={setSignupOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="signup-dialog">
          <DialogHeader><DialogTitle className="font-heading text-2xl">Join StoreIG</DialogTitle></DialogHeader>
          <DialogDescription className="text-sm text-slate-500 -mt-2 font-body">Tell us about your brand and we'll help you launch your Instagram-style store.</DialogDescription>
          <SignupForm onSuccess={() => setSignupOpen(false)} />
        </DialogContent>
      </Dialog>

      <style>{`.sinput{width:100%;border:1px solid #e4e4e7;border-radius:12px;padding:10px 14px;font-size:14px;outline:none;font-family:inherit}.sinput:focus{border-color:#000}`}</style>
      <CookieConsent />
    </div>
  );
}

const SField = ({ label, children }) => (
  <div>
    <label className="text-xs font-heading font-bold uppercase tracking-[0.15em] text-slate-500 block mb-1.5">{label}</label>
    {children}
  </div>
);

const ReviewsSlider = ({ heading, reviews }) => {
  const [idx, setIdx] = useState(0);
  const go = (d) => setIdx((i) => (i + d + reviews.length) % reviews.length);
  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % reviews.length), 5000);
    return () => clearInterval(t);
  }, [reviews.length]);
  const r = reviews[idx];
  return (
    <section className="relative z-10 max-w-4xl mx-auto w-full px-6 pt-28 md:pt-32" data-testid="reviews-section">
      <div className="text-center mb-12">
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">Wall of love</span>
        <h2 className="font-heading font-medium text-3xl md:text-5xl tracking-tight mt-2">{heading}</h2>
      </div>
      <div className="relative">
        {/* ambient glow */}
        <div className="pointer-events-none absolute inset-x-10 -inset-y-6 casper-gradient opacity-10 blur-3xl rounded-[3rem] -z-10" aria-hidden />
        <div className="relative min-h-[240px] flex items-center justify-center">
          <AnimatePresence mode="wait">
            <motion.div key={idx} initial={{ opacity: 0, y: 20, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -20, scale: 0.98 }} transition={{ duration: 0.4 }}
              className="bg-white/70 backdrop-blur-2xl border border-white/60 rounded-[2rem] shadow-[0_20px_60px_rgba(0,0,0,0.08)] px-8 md:px-12 py-10 text-center w-full"
              data-testid="review-slide">
              <div className="flex justify-center gap-1 mb-5">
                {[...Array(Math.max(0, Math.min(5, r.rating || 5)))].map((_, i) => (
                  <Star key={`st-${i}`} size={18} className="fill-amber-400 stroke-amber-400" />
                ))}
              </div>
              <p className="font-heading font-light text-2xl md:text-3xl leading-snug tracking-tight text-slate-900">"{r.text}"</p>
              <div className="mt-7 flex items-center justify-center gap-3">
                <div className="h-11 w-11 rounded-full casper-gradient flex items-center justify-center text-white font-heading font-semibold">
                  {(r.name || "?").charAt(0)}
                </div>
                <div className="text-left font-body">
                  <div className="font-semibold text-slate-900">{r.name}</div>
                  <div className="text-slate-400 text-sm">{r.handle}</div>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
        <div className="flex items-center justify-center gap-4 mt-8">
          <button onClick={() => go(-1)} data-testid="review-prev" className="h-11 w-11 rounded-full bg-white/70 backdrop-blur-xl border border-white/60 shadow-sm hover:shadow-md text-slate-700 flex items-center justify-center transition-shadow active:scale-95"><ChevronLeft size={18} /></button>
          <div className="flex gap-2">
            {reviews.map((_, i) => (
              <button key={`dot-${i}`} onClick={() => setIdx(i)} data-testid={`review-dot-${i}`} className={`h-2 rounded-full transition-all ${i === idx ? "w-7 casper-gradient" : "w-2 bg-slate-300"}`} />
            ))}
          </div>
          <button onClick={() => go(1)} data-testid="review-next" className="h-11 w-11 rounded-full bg-white/70 backdrop-blur-xl border border-white/60 shadow-sm hover:shadow-md text-slate-700 flex items-center justify-center transition-shadow active:scale-95"><ChevronRight size={18} /></button>
        </div>
      </div>
    </section>
  );
};
